import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Download, RefreshCw, ChevronDown, ChevronUp,
  CheckCircle, AlertTriangle, TrendingUp, Calendar, Euro,
  Cpu, Zap, ArrowRight, ExternalLink, Mail,
  BarChart3, Target, Lightbulb, Clock, Shield
} from 'lucide-react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Radar, ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  Tooltip, Cell
} from 'recharts';

const ResultsPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [fullReport, setFullReport] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedSections, setExpandedSections] = useState({});
  const [regenerating, setRegenerating] = useState(false);
  const [brandingConfig, setBrandingConfig] = useState(null);

  // Fetch initial results
  useEffect(() => {
    fetchResults();
    fetchBranding();
  }, [sessionId]);

  const fetchBranding = async () => {
    try {
      const res = await fetch('/api/config/branding');
      const data = await res.json();
      if (data.success) {
        setBrandingConfig(data.data);
      }
    } catch (err) {
      console.error('Erreur chargement branding:', err);
    }
  };

  const fetchResults = async () => {
    try {
      const res = await fetch(`/api/reports/${sessionId}`);
      const data = await res.json();

      if (!data.success) {
        throw new Error(data.error || 'Erreur chargement résultats');
      }

      setResults(data.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchFullReport = async (regenerate = false) => {
    setRegenerating(true);
    try {
      const url = `/api/reports/${sessionId}/full${regenerate ? '?regenerate=true' : ''}`;
      const res = await fetch(url);
      const data = await res.json();

      if (data.success) {
        setFullReport(data.data);
      }
    } catch (err) {
      console.error('Erreur génération rapport:', err);
    } finally {
      setRegenerating(false);
    }
  };

  const fetchRecommendations = async () => {
    try {
      const res = await fetch(`/api/reports/${sessionId}/recommendations`);
      const data = await res.json();

      if (data.success) {
        setRecommendations(data.data);
      }
    } catch (err) {
      console.error('Erreur recommandations:', err);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="loader mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement de vos résultats...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Erreur</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={() => navigate('/')} className="btn btn-primary">
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  const { analysis, companyInfo, sector } = results;
  const maturityScore = analysis?.maturityScore || 0;
  const maturityLevel = analysis?.maturityLevel || 'intermediaire';

  // Prepare chart data — sous-scores réels ou null si anciennes sessions
  const hasSubScores = analysis?.skillsScore !== undefined;

  const radarData = hasSubScores ? [
    { subject: 'Maturité digitale', value: maturityScore, fullMark: 100 },
    { subject: 'Compétences', value: analysis.skillsScore, fullMark: 100 },
    { subject: 'Infrastructure', value: analysis.infraScore, fullMark: 100 },
    { subject: 'Data readiness', value: analysis.dataScore, fullMark: 100 },
    { subject: 'Culture innovation', value: analysis.cultureScore, fullMark: 100 },
  ] : null;

  const getScoreColor = (score) => {
    if (score >= 80) return '#22c55e';
    if (score >= 60) return '#84cc16';
    if (score >= 40) return '#eab308';
    if (score >= 20) return '#f97316';
    return '#ef4444';
  };

  const getLevelLabel = (level) => {
    const labels = {
      novice: 'Novice',
      debutant: 'Débutant',
      intermediaire: 'Intermédiaire',
      avance: 'Avancé',
      expert: 'Expert'
    };
    return labels[level] || level;
  };

  const getLevelDescription = (level) => {
    const descriptions = {
      novice: 'Votre organisation débute sa transformation digitale. De nombreuses opportunités d\'amélioration existent.',
      debutant: 'Les bases sont posées, mais l\'IA reste à explorer. Des quick wins sont possibles.',
      intermediaire: 'Bonne maturité digitale. Vous êtes prêt pour des projets IA structurés.',
      avance: 'Maturité élevée. Focus sur l\'optimisation et l\'innovation.',
      expert: 'Excellence digitale. Explorez l\'IA de pointe et la R&D.'
    };
    return descriptions[level] || '';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-primary-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold text-gray-900">Ydv<span className="brand-gradient">Systems</span></span>
            <span className="text-[#d1d1d6]">|</span>
            <span className="text-xl font-bold text-[#636366]">Audit IA</span>
          </div>

          <button
            onClick={() => navigate('/')}
            className="btn btn-ghost text-sm"
          >
            Nouveau diagnostic
          </button>
        </div>
      </header>

      {/* Hero Section - Score */}
      <section className="max-w-7xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Votre Diagnostic IA {brandingConfig?.companyName ? `par ${brandingConfig.companyName}` : ''}
          </h1>
          <p className="text-gray-600 mb-2">
            {companyInfo?.company_name || 'Votre entreprise'} • Secteur {sector}
          </p>

        </motion.div>

        {/* Score Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="card max-w-2xl mx-auto text-center mb-12"
        >
          <div className="relative inline-block mb-6">
            {/* Circular progress */}
            <svg className="w-48 h-48 transform -rotate-90">
              <circle
                cx="96"
                cy="96"
                r="88"
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="12"
              />
              <motion.circle
                cx="96"
                cy="96"
                r="88"
                fill="none"
                stroke={getScoreColor(maturityScore)}
                strokeWidth="12"
                strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 88}
                initial={{ strokeDashoffset: 2 * Math.PI * 88 }}
                animate={{ strokeDashoffset: 2 * Math.PI * 88 * (1 - maturityScore / 100) }}
                transition={{ duration: 1.5, ease: "easeOut" }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <motion.span
                className="text-5xl font-bold"
                style={{ color: getScoreColor(maturityScore) }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                {maturityScore}
              </motion.span>
              <span className="text-gray-500 text-sm">/100</span>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Niveau : {getLevelLabel(maturityLevel)}
          </h2>
          <p className="text-gray-600 max-w-md mx-auto">
            {getLevelDescription(maturityLevel)}
          </p>
        </motion.div>

        {/* CTA Banner */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="max-w-3xl mx-auto mb-10 p-6 bg-gradient-to-r from-primary-500 to-primary-700 rounded-2xl text-white text-center shadow-lg"
        >
          <p className="text-lg font-medium mb-2">
            Votre score est de {maturityScore}/100. Vous voulez savoir comment passer au niveau superieur ?
          </p>
          <p className="text-primary-100 mb-4">
            Demandez votre rapport complet gratuit avec roadmap et estimation budgetaire.
          </p>
          {brandingConfig?.bookingUrl ? (
            <a
              href={brandingConfig.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-primary-800 font-semibold px-6 py-3 rounded-lg hover:bg-primary-50 transition-colors"
            >
              <Calendar className="w-5 h-5" />
              Prendre rendez-vous
            </a>
          ) : (
            <a
              href={`mailto:${brandingConfig?.email || ''}`}
              className="inline-flex items-center gap-2 bg-white text-primary-800 font-semibold px-6 py-3 rounded-lg hover:bg-primary-50 transition-colors"
            >
              <Mail className="w-5 h-5" />
              Prendre rendez-vous
            </a>
          )}
        </motion.div>

        {/* Tabs */}
        <div className="flex justify-center gap-2 mb-8 flex-wrap">
          {[
            { id: 'overview', label: 'Vue d\'ensemble', icon: BarChart3 },
            { id: 'opportunities', label: 'Opportunités', icon: Lightbulb },
            { id: 'roadmap', label: 'Feuille de route', icon: Calendar },
            { id: 'report', label: 'Rapport complet', icon: Download },
            { id: 'services', label: 'Services recommandés', icon: Target }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === 'services' && !recommendations) fetchRecommendations();
              }}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg transition-all
                ${activeTab === tab.id
                  ? 'bg-primary-500 text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-gray-50'}
              `}
            >
              <tab.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="max-w-5xl mx-auto">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="grid md:grid-cols-2 gap-6"
            >
              {/* Radar Chart */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Profil de maturité
                </h3>
                {radarData ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <RadarChart data={radarData}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12 }} />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} />
                      <Radar
                        name="Score"
                        dataKey="value"
                        stroke="#00bcd4"
                        fill="#00bcd4"
                        fillOpacity={0.3}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>Profil détaillé non disponible pour cette session.</p>
                    <p className="text-sm mt-2">Regénérez votre rapport pour voir le profil détaillé.</p>
                  </div>
                )}
              </div>

              {/* Strengths & Weaknesses */}
              <div className="space-y-6">
                {/* Strengths */}
                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Points forts
                  </h3>
                  <ul className="space-y-2">
                    {(analysis?.topStrengths || ['Données en cours d\'analyse...']).map((strength, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-700">
                        <span className="text-green-500 mt-1">✓</span>
                        {strength}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Weaknesses */}
                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    Axes d'amélioration
                  </h3>
                  <ul className="space-y-2">
                    {(analysis?.topWeaknesses || ['Données en cours d\'analyse...']).map((weakness, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-700">
                        <span className="text-amber-500 mt-1">!</span>
                        {weakness}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          )}

          {/* Opportunities Tab */}
          {activeTab === 'opportunities' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
            >
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">
                  Opportunites IA identifiees
                </h3>

                <div className="space-y-4">
                  {(analysis?.topOpportunities || []).map((opp, index) => (
                    <div
                      key={index}
                      className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-gray-900">
                          {opp.name || opp}
                        </h4>
                        <span className="text-2xl font-bold text-primary-500">
                          #{index + 1}
                        </span>
                      </div>
                    </div>
                  ))}

                  {(!analysis?.topOpportunities?.length) && (
                    <p className="text-gray-500 text-center py-8">
                      Aucune opportunite identifiee pour le moment.
                    </p>
                  )}
                </div>

                <div className="mt-6 p-4 bg-primary-50 rounded-lg text-center">
                  <p className="text-gray-700 mb-3">
                    Obtenez l'analyse detaillee de chaque opportunite avec estimation d'impact et complexite.
                  </p>
                  {brandingConfig?.bookingUrl ? (
                    <a
                      href={brandingConfig.bookingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary inline-flex items-center gap-2"
                    >
                      <Calendar className="w-4 h-4" />
                      Prendre rendez-vous
                    </a>
                  ) : (
                    <a
                      href={`mailto:${brandingConfig?.email || ''}`}
                      className="btn btn-primary inline-flex items-center gap-2"
                    >
                      <Mail className="w-4 h-4" />
                      Demander le rapport complet
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Roadmap Tab */}
          {activeTab === 'roadmap' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="card text-center py-12"
            >
              <Shield className="w-16 h-16 text-primary-300 mx-auto mb-6" />
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Feuille de route personnalisee
              </h3>
              <p className="text-gray-600 max-w-lg mx-auto mb-6">
                Votre roadmap personnalisee avec planning, estimation budgetaire et quick wins sera disponible apres un echange avec notre consultant.
              </p>
              {brandingConfig?.bookingUrl ? (
                <a
                  href={brandingConfig.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary inline-flex items-center gap-2"
                >
                  <Calendar className="w-4 h-4" />
                  Prendre rendez-vous
                </a>
              ) : (
                <a
                  href={`mailto:${brandingConfig?.email || ''}`}
                  className="btn btn-primary inline-flex items-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  Prendre rendez-vous
                </a>
              )}
            </motion.div>
          )}

          {/* Full Report Tab */}
          {activeTab === 'report' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="card text-center py-12"
            >
              <Download className="w-16 h-16 text-primary-300 mx-auto mb-6" />
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Rapport complet
              </h3>
              <p className="text-gray-600 max-w-lg mx-auto mb-4">
                Votre rapport complet comprend une analyse detaillee, des recommandations personnalisees,
                une feuille de route avec planning et une estimation budgetaire.
              </p>
              <p className="text-gray-500 max-w-lg mx-auto mb-6">
                Pour y acceder, prenez rendez-vous avec notre consultant qui vous le remettra
                lors de votre echange gratuit et sans engagement.
              </p>
              {brandingConfig?.bookingUrl ? (
                <a
                  href={brandingConfig.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary inline-flex items-center gap-2"
                >
                  <Calendar className="w-4 h-4" />
                  Demander le rapport complet
                </a>
              ) : (
                <a
                  href={`mailto:${brandingConfig?.email || ''}`}
                  className="btn btn-primary inline-flex items-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  Demander le rapport complet
                </a>
              )}

              {/* Bouton télécharger PDF résumé */}
              <div className="mt-6">
                <button
                  onClick={() => window.open(`/api/reports/${sessionId}/pdf`)}
                  className="btn btn-secondary inline-flex items-center gap-2 text-sm"
                >
                  <Download className="w-4 h-4" />
                  Telecharger le resume PDF
                </button>
              </div>
            </motion.div>
          )}

          {/* Services Tab */}
          {activeTab === 'services' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
            >
              {!recommendations && (
                <div className="card text-center py-12">
                  <Target className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">
                    Chargement des recommandations de services...
                  </p>
                  <div className="loader mx-auto"></div>
                </div>
              )}

              {recommendations?.customPitch && (
                <div className="card bg-gradient-to-r from-primary-500 to-primary-700 text-white">
                  <h3 className="text-lg font-semibold mb-2">Notre recommandation</h3>
                  <p>{recommendations.customPitch}</p>
                </div>
              )}

              {recommendations?.recommendedServices && (
                <div className="grid md:grid-cols-2 gap-4">
                  {recommendations.recommendedServices.map((service, index) => (
                    <div key={index} className="card hover:shadow-lg transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="font-semibold text-gray-900">
                          {service.serviceName}
                        </h4>
                        <span className="text-sm font-medium text-primary-500">
                          {service.relevanceScore}%
                        </span>
                      </div>

                      {/* Jauge de pertinence */}
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                        <div
                          className="h-2 rounded-full transition-all"
                          style={{
                            width: `${service.relevanceScore || 0}%`,
                            backgroundColor: (service.relevanceScore || 0) >= 80 ? '#22c55e'
                              : (service.relevanceScore || 0) >= 50 ? '#eab308' : '#f97316'
                          }}
                        />
                      </div>

                      {/* Argumentaire personnalisé */}
                      <p className="text-gray-700 mb-4 font-medium">
                        {service.justification}
                      </p>

                      {/* Tarif et durée */}
                      <div className="flex flex-wrap gap-3 mb-4 text-sm text-gray-600">
                        {service.priceRange && (
                          <span className="flex items-center gap-1">
                            <Euro className="w-4 h-4" />
                            {service.priceRange.min?.toLocaleString('fr-FR')}–{service.priceRange.max?.toLocaleString('fr-FR')} €
                            {service.priceRange.unit && ` ${service.priceRange.unit}`}
                          </span>
                        )}
                        {service.duration && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {service.duration}
                          </span>
                        )}
                      </div>

                      {service.estimatedValue && (
                        <p className="text-sm text-gray-500 mb-4">
                          Valeur estimée : {service.estimatedValue}
                        </p>
                      )}

                      {/* CTA Demander un devis */}
                      <button
                        onClick={() => window.location.href = `mailto:?subject=Demande de devis - ${encodeURIComponent(service.serviceName)}&body=${encodeURIComponent(service.callToAction || `Je souhaite un devis pour le service "${service.serviceName}".`)}`}
                        className="w-full btn btn-primary text-sm flex items-center justify-center gap-2"
                      >
                        <Mail className="w-4 h-4" />
                        Demander un devis
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* CTA */}
              <div className="card text-center bg-gray-50">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Prêt à passer à l'action ?
                </h3>
                <p className="text-gray-600 mb-4">
                  Discutons de votre projet IA et construisons ensemble votre feuille de route.
                </p>
                {brandingConfig?.bookingUrl ? (
                  <a
                    href={brandingConfig.bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary inline-flex items-center gap-2"
                  >
                    <Calendar className="w-4 h-4" />
                    Prendre rendez-vous
                  </a>
                ) : (
                  <a
                    href={`mailto:${brandingConfig?.email || ''}`}
                    className="btn btn-primary inline-flex items-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    Demander un rendez-vous
                  </a>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-100 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-gray-500">
          <p>Diagnostic généré par intelligence artificielle {brandingConfig?.companyName ? `— ${brandingConfig.companyName}` : ''}</p>
          {brandingConfig?.website && (
            <p className="mt-1">
              <a href={brandingConfig.website} target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:underline">
                {brandingConfig.website}
              </a>
            </p>
          )}
        </div>
      </footer>
    </div>
  );
};

export default ResultsPage;
